<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionFilterSelect from 'dashboard/components-next/Campaigns/KaptionFilterSelect.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { searchContacts } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  looksLikePhoneNumber,
  normalizeToE164,
  deriveDefaultCountry,
  formatPhoneAsYouType,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';

const props = defineProps({
  recipient: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: null },
  waInboxes: { type: Array, default: () => [] },
  inferredCountry: { type: String, default: '' },
  labels: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:recipient', 'update:inbox', 'next']);

const { t } = useI18n();

const query = ref(props.recipient?.rawInput || '');
const results = ref([]);
const country = ref(props.inferredCountry || deriveDefaultCountry(props.inbox));
const showContactForm = ref(Boolean(props.recipient?.contactDraft));
const contactDraft = ref({
  firstName: '',
  lastName: '',
  email: '',
  labels: [],
  note: '',
  ...props.recipient?.contactDraft,
});
let searchSequence = 0;
const isSearching = ref(false);
const showInboxSwitcher = ref(false);
const inboxChipRef = ref(null);
const inboxSwitcherRef = ref(null);
const inboxSwitcherStyle = ref({ visibility: 'hidden' });

const VIEWPORT_MARGIN = 8;
const SWITCHER_GAP = 4;
const SWITCHER_WIDTH = 192;
const SWITCHER_MAX_HEIGHT = 240;

const defaultCountry = computed(
  () => country.value || deriveDefaultCountry(props.inbox)
);

const isNumber = computed(() => looksLikePhoneNumber(query.value));

const parsedNumber = computed(() =>
  isNumber.value
    ? normalizeToE164(query.value, defaultCountry.value)
    : { e164: '', isValid: false, national: '' }
);

const selectedContact = computed(() => props.recipient?.contact || null);

const canProceed = computed(() => {
  if (selectedContact.value?.id) return Boolean(props.inbox);
  return (
    isNumber.value &&
    parsedNumber.value.isValid &&
    showContactForm.value &&
    contactDraft.value.firstName.trim() &&
    Boolean(props.inbox)
  );
});

// new-conversation-modal.RECIPIENT.2 — search every input mode and ignore any
// response whose sequence predates the latest keystroke.
const runSearch = debounce(async (value, sequence) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    results.value = [];
    isSearching.value = false;
    return;
  }
  isSearching.value = true;
  try {
    const found = await searchContacts({
      keys: ['name', 'email', 'phone_number'],
      query: looksLikePhoneNumber(trimmed)
        ? normalizeToE164(trimmed, defaultCountry.value).e164 || trimmed
        : trimmed,
    });
    if (sequence === searchSequence) {
      results.value = found.filter(contact => contact.phoneNumber);
    }
  } catch (error) {
    if (sequence === searchSequence) results.value = [];
  } finally {
    if (sequence === searchSequence) isSearching.value = false;
  }
}, 300);

// Fired only on real user keystrokes (not programmatic selection), so
// selecting a contact does not immediately null itself out.
const onType = () => {
  let value = query.value;
  if (looksLikePhoneNumber(value)) {
    value = formatPhoneAsYouType(value, defaultCountry.value);
    query.value = value;
    const international = parsePhoneNumberFromString(
      value.startsWith('00') ? `+${value.slice(2)}` : value
    );
    if (
      (value.startsWith('+') || value.startsWith('00')) &&
      international?.country
    ) {
      country.value = international.country;
    }
  }
  showContactForm.value = false;
  emit('update:recipient', {
    ...props.recipient,
    rawInput: value,
    contact: null,
    mode: looksLikePhoneNumber(value) ? 'number' : 'contact',
    e164: parsedNumber.value.e164,
    isValid: parsedNumber.value.isValid,
  });
  searchSequence += 1;
  runSearch(value, searchSequence);
};

const selectContact = contact => {
  searchSequence += 1;
  isSearching.value = false;
  query.value = contact.name || contact.phoneNumber || contact.email;
  results.value = [];
  emit('update:recipient', {
    ...props.recipient,
    rawInput: query.value,
    mode: 'contact',
    contact,
    e164: contact.phoneNumber || '',
    isValid: true,
  });
};

const selectNewNumber = () => {
  emit('update:recipient', {
    ...props.recipient,
    rawInput: query.value,
    mode: 'number',
    contact: null,
    e164: parsedNumber.value.e164,
    isValid: parsedNumber.value.isValid,
    contactDraft: contactDraft.value,
  });
};

const startNewContact = () => {
  searchSequence += 1;
  isSearching.value = false;
  showContactForm.value = true;
  results.value = [];
  selectNewNumber();
};

const chooseInbox = inbox => {
  showInboxSwitcher.value = false;
  emit('update:inbox', inbox);
};

const updateInboxSwitcherPosition = () => {
  if (!showInboxSwitcher.value || !inboxChipRef.value) return;

  const triggerRect = inboxChipRef.value.getBoundingClientRect();
  const panelHeight = Math.min(
    inboxSwitcherRef.value?.scrollHeight || SWITCHER_MAX_HEIGHT,
    SWITCHER_MAX_HEIGHT
  );
  const availableBelow = window.innerHeight - triggerRect.bottom;
  const availableAbove = triggerRect.top;
  const opensAbove =
    availableBelow < panelHeight + SWITCHER_GAP + VIEWPORT_MARGIN &&
    availableAbove > availableBelow;
  const availableHeight = opensAbove
    ? availableAbove - SWITCHER_GAP - VIEWPORT_MARGIN
    : availableBelow - SWITCHER_GAP - VIEWPORT_MARGIN;
  const maxHeight = Math.max(
    80,
    Math.min(SWITCHER_MAX_HEIGHT, availableHeight)
  );
  const renderedHeight = Math.min(panelHeight, maxHeight);
  const top = opensAbove
    ? triggerRect.top - renderedHeight - SWITCHER_GAP
    : triggerRect.bottom + SWITCHER_GAP;
  const width = Math.min(
    SWITCHER_WIDTH,
    window.innerWidth - VIEWPORT_MARGIN * 2
  );
  const left = Math.min(
    Math.max(VIEWPORT_MARGIN, triggerRect.left),
    window.innerWidth - width - VIEWPORT_MARGIN
  );

  inboxSwitcherStyle.value = {
    top: `${Math.max(VIEWPORT_MARGIN, top)}px`,
    left: `${left}px`,
    width: `${width}px`,
    maxHeight: `${maxHeight}px`,
    visibility: 'visible',
  };
};

const toggleInboxSwitcher = () => {
  showInboxSwitcher.value = !showInboxSwitcher.value;
};

const handleInboxSwitcherPointerDown = event => {
  if (
    inboxChipRef.value?.contains(event.target) ||
    inboxSwitcherRef.value?.contains(event.target)
  ) {
    return;
  }
  showInboxSwitcher.value = false;
};

const handleInboxSwitcherKeydown = event => {
  if (event.key === 'Escape') showInboxSwitcher.value = false;
};

const addInboxSwitcherListeners = () => {
  document.addEventListener('pointerdown', handleInboxSwitcherPointerDown);
  document.addEventListener('keydown', handleInboxSwitcherKeydown);
  window.addEventListener('resize', updateInboxSwitcherPosition);
  window.addEventListener('scroll', updateInboxSwitcherPosition, true);
};

const removeInboxSwitcherListeners = () => {
  document.removeEventListener('pointerdown', handleInboxSwitcherPointerDown);
  document.removeEventListener('keydown', handleInboxSwitcherKeydown);
  window.removeEventListener('resize', updateInboxSwitcherPosition);
  window.removeEventListener('scroll', updateInboxSwitcherPosition, true);
};

const goNext = () => {
  if (
    !selectedContact.value &&
    isNumber.value &&
    parsedNumber.value.isValid &&
    !showContactForm.value
  ) {
    startNewContact();
    return;
  }
  if (!canProceed.value) return;
  if (isNumber.value && !selectedContact.value) selectNewNumber();
  emit('next');
};

onMounted(() => {
  searchSequence += 1;
  if (query.value) runSearch(query.value, searchSequence);
});

watch(
  () => props.inferredCountry,
  value => {
    if (value && !String(query.value).startsWith('+')) country.value = value;
  }
);

watch(country, () => {
  if (query.value && !String(query.value).startsWith('+')) onType();
});

watch(
  contactDraft,
  draft => {
    if (!showContactForm.value) return;
    emit('update:recipient', {
      ...props.recipient,
      rawInput: query.value,
      mode: 'number',
      contact: null,
      e164: parsedNumber.value.e164,
      isValid: parsedNumber.value.isValid,
      contactDraft: { ...draft },
    });
  },
  { deep: true }
);

watch(showInboxSwitcher, async isOpen => {
  removeInboxSwitcherListeners();
  if (!isOpen) return;

  inboxSwitcherStyle.value = { visibility: 'hidden' };
  addInboxSwitcherListeners();
  await nextTick();
  updateInboxSwitcherPosition();
});

onBeforeUnmount(removeInboxSwitcherListeners);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <label for="new-conversation-recipient" class="sr-only">
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.LABEL') }}
      </label>
      <div class="relative">
        <span
          class="i-lucide-user-round-search absolute size-4 -translate-y-1/2 left-3 top-1/2 text-n-slate-10"
        />
        <input
          id="new-conversation-recipient"
          v-model="query"
          type="text"
          data-testid="recipient-input"
          autofocus
          :placeholder="t('NEW_CONVERSATION_MODAL.RECIPIENT.PLACEHOLDER')"
          class="reset-base w-full h-10 pl-9 pr-3 text-sm bg-n-alpha-black2 border border-n-weak rounded-lg outline-none text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand"
          @input="onType"
          @keydown.enter.prevent="goNext"
        />

        <!-- Suggestions -->
        <div
          v-if="
            query &&
            !showContactForm &&
            !selectedContact &&
            (results.length || isSearching || isNumber)
          "
          class="absolute z-20 w-full mt-1 overflow-hidden bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-lg shadow-lg"
        >
          <div
            v-if="isSearching"
            class="flex items-center gap-2 px-3 py-2.5 text-sm text-n-slate-11"
          >
            <Spinner class="size-4" />
            {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.SEARCHING') }}
          </div>

          <!-- New number row (create-on-send) -->
          <button
            v-if="isNumber && parsedNumber.isValid"
            type="button"
            data-testid="new-number-row"
            class="flex items-center w-full gap-3 px-3 py-2.5 text-left hover:bg-n-alpha-black2"
            @click="startNewContact"
          >
            <span
              class="flex items-center justify-center rounded-full size-8 bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-sparkles size-4" />
            </span>
            <span class="min-w-0">
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{
                  t('NEW_CONVERSATION_MODAL.RECIPIENT.MESSAGE_NUMBER', {
                    number: parsedNumber.national || parsedNumber.e164,
                  })
                }}
              </span>
              <span class="block text-xs text-n-slate-10">
                {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.NEW_CONTACT') }}
              </span>
            </span>
          </button>

          <!-- Existing contact suggestions -->
          <button
            v-for="contact in results"
            :key="contact.id"
            type="button"
            class="flex items-center w-full gap-3 px-3 py-2.5 text-left hover:bg-n-alpha-black2"
            @click="selectContact(contact)"
          >
            <Avatar :name="contact.name" :src="contact.thumbnail" :size="32" />
            <span class="min-w-0">
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{ contact.name }}
              </span>
              <span class="block text-xs truncate text-n-slate-10">
                {{ contact.phoneNumber || contact.email }}
              </span>
            </span>
          </button>
        </div>
      </div>
      <p class="mt-1.5 text-xs text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.HINT') }}
      </p>
      <label
        v-if="isNumber"
        class="flex items-center gap-2 mt-2 text-xs text-n-slate-10"
      >
        <span>{{ t('NEW_CONVERSATION_MODAL.RECIPIENT.COUNTRY') }}</span>
        <KaptionFilterSelect
          v-model="country"
          data-testid="country-selector"
          :options="
            countries.map(item => ({
              value: item.id,
              label: `${item.emoji} ${item.name} (${item.dial_code})`,
            }))
          "
          :placeholder="t('NEW_CONVERSATION_MODAL.RECIPIENT.COUNTRY')"
        />
        <span v-if="parsedNumber.e164" class="font-medium text-n-slate-11">
          {{ parsedNumber.e164 }}
        </span>
      </label>
    </div>

    <!-- new-conversation-modal.RECIPIENT.3 — mobile-parity contact details -->
    <div
      v-if="showContactForm"
      data-testid="new-contact-form"
      class="grid grid-cols-1 gap-3 p-4 border sm:grid-cols-2 rounded-xl border-n-weak bg-n-alpha-black2"
    >
      <label class="flex flex-col gap-1 text-xs text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.FIRST_NAME') }}
        <input
          v-model="contactDraft.firstName"
          autofocus
          class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg outline-none border-n-weak text-n-slate-12 focus:border-n-brand"
          data-testid="contact-first-name"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.LAST_NAME') }}
        <input
          v-model="contactDraft.lastName"
          class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg outline-none border-n-weak text-n-slate-12 focus:border-n-brand"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-10 sm:col-span-2">
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.EMAIL') }}
        <input
          v-model="contactDraft.email"
          type="email"
          class="h-9 px-3 text-sm bg-n-solid-1 border rounded-lg outline-none border-n-weak text-n-slate-12 focus:border-n-brand"
        />
      </label>
      <label
        v-if="labels.length"
        class="flex flex-col gap-1 text-xs text-n-slate-10 sm:col-span-2"
      >
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.LABELS') }}
        <KaptionFilterSelect
          v-model="contactDraft.labels"
          multiple
          :options="
            labels.map(label => ({ value: label.title, label: label.title }))
          "
          :placeholder="t('NEW_CONVERSATION_MODAL.RECIPIENT.LABELS')"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-10 sm:col-span-2">
        {{ t('NEW_CONVERSATION_MODAL.RECIPIENT.NOTE') }}
        <textarea
          v-model="contactDraft.note"
          rows="2"
          class="px-3 py-2 text-sm bg-n-solid-1 border rounded-lg outline-none resize-none border-n-weak text-n-slate-12 focus:border-n-brand"
        />
      </label>
    </div>

    <!-- Inbox chip / switcher -->
    <div v-if="inbox" class="flex items-center gap-2">
      <span class="text-xs text-n-slate-10">{{
        t('NEW_CONVERSATION_MODAL.RECIPIENT.VIA')
      }}</span>
      <div class="relative">
        <button
          ref="inboxChipRef"
          type="button"
          data-testid="inbox-chip"
          class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-n-alpha-black2 text-n-slate-12 hover:bg-n-alpha-black4"
          :disabled="waInboxes.length <= 1"
          :aria-expanded="showInboxSwitcher"
          aria-haspopup="listbox"
          @click="toggleInboxSwitcher"
        >
          <span class="i-ri-whatsapp-fill size-3.5 text-n-teal-10" />
          {{ inbox.name }}
          <span
            v-if="waInboxes.length > 1"
            class="i-lucide-chevron-down size-3"
          />
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showInboxSwitcher && waInboxes.length > 1"
        ref="inboxSwitcherRef"
        data-testid="inbox-switcher"
        role="listbox"
        :style="inboxSwitcherStyle"
        class="fixed z-[60] overflow-y-auto bg-n-solid-1 border border-n-strong rounded-lg shadow-lg"
      >
        <button
          v-for="waInbox in waInboxes"
          :key="waInbox.id"
          type="button"
          role="option"
          :aria-selected="waInbox.id === inbox.id"
          class="flex items-center w-full gap-2 px-3 py-2 text-sm text-left hover:bg-n-alpha-black2 text-n-slate-12"
          @click="chooseInbox(waInbox)"
        >
          <span class="i-ri-whatsapp-fill size-4 text-n-teal-10" />
          <span class="truncate">{{ waInbox.name }}</span>
        </button>
      </div>
    </Teleport>

    <div class="flex justify-end pt-2">
      <Button
        :label="t('NEW_CONVERSATION_MODAL.NEXT_TEMPLATE')"
        trailing-icon
        icon="i-lucide-arrow-right"
        size="sm"
        data-testid="recipient-next"
        :disabled="!canProceed"
        @click="goNext"
      />
    </div>
  </div>
</template>
